import React, { useEffect, useState, useCallback } from 'react';
import {
  ArrowLeft, Palette, Save, Loader2, AlertCircle, CheckCircle, FileText, RefreshCw,
  Plus, Trash2, Copy
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { PrinterSelect } from '../../../common/PrinterSelect';
import { useAuth } from '../../../../contexts/AuthContext';
import reportConfigurationService, {
  ReportConfiguration,
  ReportType,
  ReportTypeLabels,
  StandardReportInfo
} from '../../../../services/reportConfigurationService';
import type { LabelLayout } from '../../../../types/labelDesign';
import { LabelDesigner } from '../administration/label-designer/LabelDesigner';
import { TemplatePickerDialog } from './TemplatePickerDialog';

interface RapportDesignerProps {
  onBack: () => void;
}

type EditorMode =
  | { kind: 'create'; draft: DraftMeta }
  | { kind: 'edit'; report: ReportConfiguration };

interface DraftMeta {
  reportID: number;
  reportType: number;
  reportName: string;
  description: string;
  externalPrinterWidth: number;
  externalPrinterLength: number;
  externalPrinterLandscape: boolean;
  printerName: string;
  activeYesNo: boolean;
}

const emptyDraft = (): DraftMeta => ({
  reportID: 0,
  reportType: ReportType.ItemLabel,
  reportName: '',
  description: '',
  externalPrinterWidth: 100,
  externalPrinterLength: 50,
  externalPrinterLandscape: false,
  printerName: '',
  activeYesNo: true
});

const draftFromTemplate = (t: StandardReportInfo): DraftMeta => ({
  reportID: 0,
  reportType: t.reportType,
  reportName: t.reportName,
  description: t.displayName,
  externalPrinterWidth: t.paperWidth,
  externalPrinterLength: t.paperHeight,
  externalPrinterLandscape: t.landscape,
  printerName: '',
  activeYesNo: true
});

export const RapportDesigner: React.FC<RapportDesignerProps> = ({ onBack }) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo;

  // List state
  const [reports, setReports] = useState<ReportConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [copyingId, setCopyingId] = useState<number | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [typeFilter, setTypeFilter] = useState<number | 'all'>('all');

  // Editor state
  const [editorMode, setEditorMode] = useState<EditorMode | null>(null);
  const [meta, setMeta] = useState<DraftMeta>(emptyDraft());
  const [currentLayout, setCurrentLayout] = useState<LabelLayout | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const loadReports = useCallback(async () => {
    if (!companyNo) return;
    setLoading(true);
    setLoadError(null);
    try {
      const all = await reportConfigurationService.getAll(companyNo);
      setReports(all);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Kunne ikke hente rapporter');
    } finally {
      setLoading(false);
    }
  }, [companyNo]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const openEditor = (report: ReportConfiguration) => {
    setEditorMode({ kind: 'edit', report });
    setMeta({
      reportID: report.reportID,
      reportType: report.reportType,
      reportName: report.reportName,
      description: report.description,
      externalPrinterWidth: report.externalPrinterWidth ?? 100,
      externalPrinterLength: report.externalPrinterLength ?? 50,
      externalPrinterLandscape: report.externalPrinterLandscape ?? false,
      printerName: report.printerName ?? '',
      activeYesNo: report.activeYesNo ?? true
    });
    if (report.layoutJson) {
      try {
        setCurrentLayout(JSON.parse(report.layoutJson));
      } catch {
        setCurrentLayout(null);
      }
    } else {
      setCurrentLayout(null);
    }
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleTemplatePicked = (template: StandardReportInfo | null) => {
    const draft = template ? draftFromTemplate(template) : emptyDraft();
    setShowTemplatePicker(false);
    setEditorMode({ kind: 'create', draft });
    setMeta(draft);
    setCurrentLayout(null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const closeEditor = () => {
    setEditorMode(null);
    setCurrentLayout(null);
    setSaveError(null);
    setSaveSuccess(false);
    loadReports();
  };

  const validateMeta = (): string | null => {
    if (!meta.description.trim()) return 'Beskrivelse er påkrevd';
    if (editorMode?.kind === 'create' && (!meta.reportID || meta.reportID < 1)) {
      return 'Rapport-ID må være et positivt heltall';
    }
    if (!meta.reportName.trim()) return 'Rapportnavn er påkrevd';
    if (meta.externalPrinterWidth <= 0 || meta.externalPrinterLength <= 0) {
      return 'Dimensjoner må være større enn 0';
    }
    return null;
  };

  const handleSave = async () => {
    if (!companyNo || !editorMode) return;

    const validation = validateMeta();
    if (validation) {
      setSaveError(validation);
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const layoutJson = currentLayout && currentLayout.elements.length > 0
      ? JSON.stringify(currentLayout)
      : null;

    try {
      if (editorMode.kind === 'edit') {
        await reportConfigurationService.update(companyNo, editorMode.report.id, {
          ...editorMode.report,
          ...meta,
          layoutJson
        });
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        const created = await reportConfigurationService.create(companyNo, {
          ...meta,
          layoutJson,
          companyNo,
          cloudStorageYesNo: false
        });
        setEditorMode({ kind: 'edit', report: created });
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Kunne ikke lagre');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (report: ReportConfiguration) => {
    if (!companyNo) return;

    setCopyingId(report.id);
    try {
      const copy = await reportConfigurationService.copy(companyNo, report.id);
      await loadReports();
      // Åpne editoren direkte på den nye kopien så brukeren kan justere navn/innhold
      openEditor(copy);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Kunne ikke kopiere rapporten');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDelete = async (report: ReportConfiguration) => {
    if (!companyNo) return;
    const ok = window.confirm(
      `Slette rapporten "${report.description}" (ID ${report.reportID})?\n\nDette kan ikke angres.`
    );
    if (!ok) return;

    setDeletingId(report.id);
    try {
      await reportConfigurationService.delete(companyNo, report.id);
      await loadReports();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Kunne ikke slette rapporten');
    } finally {
      setDeletingId(null);
    }
  };

  if (editorMode) {
    const isCreate = editorMode.kind === 'create';
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <button
              onClick={closeEditor}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              type="button"
            >
              <ArrowLeft size={16} />
              Tilbake til rapportliste
            </button>
            <div className="h-6 w-px bg-gray-200" />
            <div className="text-sm font-semibold text-gray-900">
              {isCreate ? 'Ny rapport' : `Rediger: ${editorMode.report.description}`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {saveSuccess && (
              <span className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle size={14} /> Lagret
              </span>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {isCreate ? 'Opprett rapport' : 'Lagre'}
            </button>
          </div>
        </div>

        {saveError && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <div>{saveError}</div>
          </div>
        )}

        {/* Metadata-panel (kompakt) */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 grid grid-cols-1 md:grid-cols-6 gap-3 text-sm">
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Rapport-ID *</label>
            <input
              type="number"
              min={1}
              value={meta.reportID || ''}
              onChange={(e) => setMeta({ ...meta, reportID: parseInt(e.target.value) || 0 })}
              disabled={!isCreate}
              className="w-full px-2 py-1.5 border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-600"
              placeholder="1"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Beskrivelse *</label>
            <input
              type="text"
              value={meta.description}
              onChange={(e) => setMeta({ ...meta, description: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded"
              placeholder="F.eks. 'Vareetikett 100x50 med QR'"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
            <select
              value={meta.reportType}
              onChange={(e) => setMeta({ ...meta, reportType: parseInt(e.target.value) })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded"
            >
              {Object.entries(ReportTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Bredde (mm)</label>
            <input
              type="number"
              step="0.1"
              value={meta.externalPrinterWidth}
              onChange={(e) => setMeta({ ...meta, externalPrinterWidth: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Lengde (mm)</label>
            <input
              type="number"
              step="0.1"
              value={meta.externalPrinterLength}
              onChange={(e) => setMeta({ ...meta, externalPrinterLength: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Rapportnavn *</label>
            <input
              type="text"
              value={meta.reportName}
              onChange={(e) => setMeta({ ...meta, reportName: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded font-mono text-xs"
              placeholder="STANDARD:ItemLabel_100x50 eller CustomLabel.fr3"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Printer (valgfri)</label>
            <PrinterSelect
              value={meta.printerName || null}
              onChange={(name) => setMeta({ ...meta, printerName: name || '' })}
              valueField="id"
              placeholder="Ingen fast printer"
              showTestButton={false}
              size="sm"
            />
          </div>
          <div className="flex items-end gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer px-2 py-1.5">
              <input
                type="checkbox"
                checked={meta.externalPrinterLandscape}
                onChange={(e) => setMeta({ ...meta, externalPrinterLandscape: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-xs font-medium text-gray-700">Liggende</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer px-2 py-1.5"
              title="Inaktive rapporter skjules fra utskriftsdialoger, men er fortsatt synlige her">
              <input
                type="checkbox"
                checked={meta.activeYesNo}
                onChange={(e) => setMeta({ ...meta, activeYesNo: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-xs font-medium text-gray-700">Aktiv</span>
            </label>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <LabelDesigner
            initialLayout={currentLayout}
            widthMm={meta.externalPrinterWidth || 100}
            heightMm={meta.externalPrinterLength || 50}
            reportType={meta.reportType}
            onChange={setCurrentLayout}
          />
        </div>
      </div>
    );
  }

  // List view
  const filteredReports = typeFilter === 'all'
    ? reports
    : reports.filter((r) => r.reportType === typeFilter);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack} />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Palette size={24} className="text-blue-600" />
              Rapport-designer
            </h1>
            <p className="text-sm text-gray-600">
              Designverktøy for egendefinerte etiketter og rapporter. Opprett nye fra mal eller rediger eksisterende layout.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="font-medium">Type:</span>
            <select
              value={typeFilter === 'all' ? 'all' : String(typeFilter)}
              onChange={(e) => setTypeFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              disabled={loading || reports.length === 0}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="all">Alle typer</option>
              {Array.from(new Set(reports.map((r) => r.reportType)))
                .sort((a, b) => a - b)
                .map((t) => (
                  <option key={t} value={t}>
                    {ReportTypeLabels[t as ReportType] ?? `Type ${t}`}
                  </option>
                ))}
            </select>
          </label>
          <button
            onClick={loadReports}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
            type="button"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Oppdater
          </button>
          <button
            onClick={() => setShowTemplatePicker(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            type="button"
          >
            <Plus size={14} />
            Ny rapport
          </button>
        </div>
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <div>{loadError}</div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={18} />
            Laster rapporter…
          </div>
        ) : reports.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p>Ingen rapporter enda.</p>
            <p className="text-xs mt-1">
              Klikk <strong>Ny rapport</strong> for å lage en fra en mal.
            </p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p>Ingen rapporter matcher det valgte filteret.</p>
            <button
              type="button"
              onClick={() => setTypeFilter('all')}
              className="text-xs text-blue-600 hover:underline mt-1"
            >
              Vis alle typer
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-gray-600">
                <th className="px-4 py-2 font-medium">ID</th>
                <th className="px-4 py-2 font-medium">Beskrivelse</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Dimensjoner</th>
                <th className="px-4 py-2 font-medium">Design</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((r) => {
                const hasDesign = !!r.layoutJson;
                const typeLabel = ReportTypeLabels[r.reportType as ReportType] || String(r.reportType);
                const isDeleting = deletingId === r.id;
                const isInactive = !r.activeYesNo;
                return (
                  <tr key={r.id} className={`border-b border-gray-200 hover:bg-gray-50 ${isInactive ? 'bg-gray-50 text-gray-500' : ''}`}>
                    <td className="px-4 py-2 font-mono">{r.reportID}</td>
                    <td className="px-4 py-2">{r.description}</td>
                    <td className="px-4 py-2">{typeLabel}</td>
                    <td className="px-4 py-2">
                      {r.externalPrinterWidth ?? '—'} × {r.externalPrinterLength ?? '—'} mm
                      {r.externalPrinterLandscape ? ' (liggende)' : ''}
                    </td>
                    <td className="px-4 py-2">
                      {hasDesign ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                          <Palette size={12} /> Egendefinert
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Standard</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {isInactive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">
                          Inaktiv
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                          Aktiv
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEditor(r)}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          type="button"
                          title={hasDesign ? 'Rediger design og metadata' : 'Design layout'}
                        >
                          <Palette size={14} />
                          {hasDesign ? 'Rediger' : 'Design'}
                        </button>
                        <button
                          onClick={() => handleCopy(r)}
                          disabled={copyingId === r.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-gray-700 hover:bg-gray-100 rounded text-sm disabled:opacity-50"
                          type="button"
                          title="Kopier rapport (oppretter ny rad med neste ledige Rapport-ID)"
                        >
                          {copyingId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
                        </button>
                        <button
                          onClick={() => handleDelete(r)}
                          disabled={isDeleting}
                          className="inline-flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm disabled:opacity-50"
                          type="button"
                          title="Slett rapport"
                        >
                          {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {companyNo && (
        <TemplatePickerDialog
          isOpen={showTemplatePicker}
          companyNo={companyNo}
          onCancel={() => setShowTemplatePicker(false)}
          onPick={handleTemplatePicked}
        />
      )}
    </div>
  );
};
